import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Chip,
} from '@mui/material';
import {
  Search,
  Person,
  Logout,
  Settings,
  VpnKey,
} from '@mui/icons-material';
import { mvkgTokens, readerTokens as R, shellTokens as SH } from '../../theme/mvkgTokens';
import { useAuth } from '../../contexts/AuthContext';
import GlobalSearch from '../GlobalSearch';
import CollectionSwitcher from './CollectionSwitcher';
import AppAvatar from '../ui/AppAvatar';
import NotificationDropdown from './NotificationDropdown';
import useNotifications from '../../hooks/useNotifications';

// The search shortcut is Ctrl+K off the Mac (the handler accepts both).
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

export const APP_TOP_BAR_HEIGHT = 72;

// The top bar names the section the page belongs to, the same word as its
// sidebar item. Entity-hub pages: a person under People, any other vertex under
// Graph (the same rule as the sidebar's active item).
const SCREEN_TITLES = [
  { match: (p) => p === '/', title: 'Home' },
  { match: (p) => p.startsWith('/articles') || p.startsWith('/digest'), title: 'Library' },
  { match: (p) => p.startsWith('/entity/author'), title: 'People' },
  {
    match: (p) => p.startsWith('/graph') || p.startsWith('/entity/')
      || ['/method/', '/dataset/', '/problem/', '/topic/', '/direction/'].some((x) => p.startsWith(x)),
    title: 'Graph',
  },
  { match: (p) => p.startsWith('/discovery'), title: 'Discovery' },
  { match: (p) => p.startsWith('/rag'), title: 'Ask' },
  { match: (p) => p.startsWith('/author-insights'), title: 'People' },
  { match: (p) => p.startsWith('/profile'), title: 'Profile' },
  { match: (p) => p.startsWith('/directions'), title: 'Directions' },
  { match: (p) => p.startsWith('/admin'), title: 'Admin' },
];

function screenTitle(pathname) {
  const row = SCREEN_TITLES.find((t) => t.match(pathname));
  return row?.title || null;
}

export default function AppTopBar({ sidebarCollapsed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  // SSE-driven notifications — UX audit 2026-05-26 cross-cutting #11.
  // The hook silently no-ops if `/api/notifications/stream` is not deployed,
  // so the dropdown still works on staging without the route.
  const notifications = useNotifications({ enabled: !!user });

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const title = screenTitle(location.pathname);
  const initials =
    user?.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    || user?.username?.slice(0, 2).toUpperCase()
    || '?';

  return (
    <>
      <Box
        component="header"
        sx={{
          // Flat, on a hairline, like the reader pages. The height stays 72: the
          // rails and the graph canvas size themselves against it.
          height: APP_TOP_BAR_HEIGHT,
          flexShrink: 0,
          px: { xs: 2, md: 3 },
          bgcolor: SH.surface,
          borderBottom: `1px solid ${SH.rule}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
          {title && (
            <Typography
              component="div"
              sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' }, minWidth: 88, whiteSpace: 'nowrap',
                fontSize: SH.pageTitle.size, fontWeight: SH.pageTitle.weight, color: SH.pageTitle.color }}
            >
              {title}
            </Typography>
          )}

          {/* With the sidebar collapsed its collection switcher is hidden: the
              same switcher sits here, left of search (never both at once). */}
          {sidebarCollapsed && <CollectionSwitcher compact />}

          {/* A button that looks like a field: it opens the search dialog. The
              shortcut sits in its own key cap, clear of the placeholder. */}
          <Box
            component="button" type="button"
            onClick={() => setSearchOpen(true)}
            aria-label={`Search papers, methods and authors (${IS_MAC ? 'Command' : 'Ctrl'}+K)`}
            aria-haspopup="dialog"
            sx={{
              display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1,
              width: 360, height: 36, px: 1.25, border: `1px solid ${R.ruleStrong}`, borderRadius: `${SH.controlRadius}px`,
              bgcolor: SH.surface, font: 'inherit', fontSize: R.size.ui, color: R.faint, cursor: 'text', textAlign: 'left',
              transition: 'border-color .15s ease',
              '&:hover': { borderColor: 'rgba(15,23,42,0.28)' },
              '&:focus-visible': { outline: `2px solid ${mvkgTokens.brand.main}`, outlineOffset: 2 },
            }}
          >
            <Search sx={{ fontSize: 18, color: R.faint }} />
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>Search…</Box>
            <Box
              component="kbd"
              sx={{
                flexShrink: 0, px: 0.75, py: 0.125, borderRadius: `${R.radius.xs}px`, bgcolor: R.chip,
                border: `1px solid ${R.rule}`, fontFamily: mvkgTokens.font.mono, fontSize: 11, color: R.muted, lineHeight: 1.6,
              }}
            >
              {IS_MAC ? '⌘K' : 'Ctrl K'}
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          <NotificationDropdown items={notifications} />

          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ p: 0.5 }}
            aria-label="Account menu"
          >
            <AppAvatar initials={initials} size="md" online ring />
          </IconButton>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" fontWeight={700}>{user?.full_name || user?.username}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{user?.email}</Typography>
          {user?.role && (
            <Chip
              size="small"
              label={user.role}
              sx={{
                mt: 0.75,
                height: 20,
                fontFamily: mvkgTokens.font.mono,
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                bgcolor: 'rgba(99,102,241,0.1)',
                color: 'primary.dark',
              }}
            />
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); navigate('/profile'); }}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          My profile
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); navigate('/profile'); }}>
          <ListItemIcon><VpnKey fontSize="small" /></ListItemIcon>
          API keys
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); navigate('/profile'); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            logout();
            navigate('/login');
          }}
        >
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
